import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { 
    deleteAllFilmImages, 
    deleteAllFilms, 
    deleteAllMusicaImages, 
    deleteAllMusica, 
    deleteAllClienti,
    getGoogleAuthStatus,
    resetAllPuntiFedelta
} from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLink } from '@fortawesome/free-solid-svg-icons';

function AdminPage() {
    const isMobile = useMediaQuery({ maxWidth: 991 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const googleAuthStatus = queryParams.get('google_auth');
        if (googleAuthStatus === 'success') {
            setSuccess("Account Google collegato con successo!");
        } else if (googleAuthStatus === 'error') {
            setError("Impossibile collegare l'account Google.");
        }
        
        if (googleAuthStatus) {
            window.history.replaceState({}, document.title, "/amministrazione");
        }

        const checkGoogleStatus = async () => {
            try {
                const response = await getGoogleAuthStatus();
                setIsGoogleConnected(response.data.is_connected);
            } catch (err) {
                console.error("Errore nel controllo dello stato di Google Auth", err);
                setIsGoogleConnected(false);
            }
        };
        checkGoogleStatus();
    }, [location.search]);

    const handleAction = async (actionFn, confirmMessage, successMessage) => {
        if (window.confirm(confirmMessage)) {
            setLoading(true);
            setError('');
            setSuccess('');
            try {
                const response = await actionFn();
                setSuccess(response.data.message || successMessage);
            } catch (err) {
                setError(err.response?.data?.detail || 'Si è verificato un errore.');
            } finally {
                setLoading(false);
            }
        }
    };
    
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const handleDeleteAllImages = () => handleAction(deleteAllFilmImages, 'Sei sicuro di voler cancellare TUTTE le immagini dei film? Questa operazione è IRREVERSIBILE.', 'Tutte le immagini dei film sono state cancellate.');
    const handleDeleteAllFilms = () => handleAction(deleteAllFilms, 'Sei sicuro di voler cancellare TUTTI i film? Questa operazione è IRREVERSIBILE.', 'Tutti i film sono stati cancellati.');
    const handleDeleteAllMusicaImages = () => handleAction(deleteAllMusicaImages, 'Sei sicuro di voler cancellare TUTTE le immagini della musica? Questa operazione è IRREVERSIBILE.', 'Tutte le immagini della musica sono state cancellate.');
    const handleDeleteAllMusica = () => handleAction(deleteAllMusica, 'Sei sicuro di voler cancellare TUTTA la musica? Questa operazione è IRREVERSIBILE.', 'Tutta la musica è stata cancellata.');
    const handleDeleteAllClienti = () => handleAction(deleteAllClienti, 'Sei sicuro di voler cancellare TUTTI i clienti? Questa operazione è IRREVERSIBILE.', 'Tutti i clienti sono stati cancellati.');
    const handleResetAllPuntiFedelta = () => handleAction(resetAllPuntiFedelta, 'Sei sicuro di voler AZZERARE TUTTI i punti fedeltà? Verranno eliminate tutte le transazioni e azzerati tutti i saldi clienti. Questa operazione è IRREVERSIBILE.', 'Tutti i punti fedeltà sono stati azzerati.');

    return (
        <Container className="mt-4">
            <style>{`
                @media (max-width: 991px) {
                    .mobile-admin-button {
                        min-height: 44px;
                        font-size: 16px;
                        padding: 12px 20px;
                        margin-bottom: 8px;
                    }
                    .mobile-admin-card {
                        margin-bottom: 1.5rem;
                    }
                    .mobile-warning-text {
                        font-size: 14px;
                        line-height: 1.4;
                    }
                }
            `}</style>
            <Row>
                <Col>
                    <h1>Pannello di Amministrazione</h1>
                    <p className="text-muted">Azioni di manutenzione e integrazioni.</p>
                </Col>
            </Row>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

            <Card className={`mt-4 ${isMobile ? 'mobile-admin-card' : ''}`}>
                <Card.Header><h5 className="mb-0">Integrazione Google</h5></Card.Header>
                <Card.Body>
                    <Card.Text className={isMobile ? 'mobile-warning-text' : ''}>
                        Collega il tuo Account Google per abilitare l'integrazione con Calendario e Attività. 
                        Questo permetterà all'applicazione di creare eventi e attività per tuo conto.
                    </Card.Text>
                    {isGoogleConnected ? (
                        <div className="d-flex align-items-center text-success">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            <span>Account Google collegato con successo.</span>
                        </div>
                    ) : (
                        <Button 
                            variant="primary" 
                            href={`${API_BASE_URL}/api/auth/google/login?purpose=app_auth`}
                            className={isMobile ? 'mobile-admin-button w-100' : ''}
                        >
                            <FontAwesomeIcon icon={faLink} className="me-2" />
                            {isMobile ? 'Collega Google' : 'Collega Account Google'}
                        </Button>
                    )}
                </Card.Body>
            </Card>

            <Card className={`mt-4 ${isMobile ? 'mobile-admin-card' : ''}`}>
                <Card.Header>
                    <h5 className="mb-0">Gestione Film</h5>
                </Card.Header>
                <Card.Body>
                    <Card.Text className={isMobile ? 'mobile-warning-text' : ''}>
                        Le seguenti azioni sono permanenti e avranno effetto su tutti i film nel database. Procedere con la massima cautela.
                    </Card.Text>
                    <div className={`d-grid gap-2 ${isMobile ? '' : 'd-md-flex justify-content-md-start'}`}>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteAllImages} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Cancella Img. Film' : 'Cancella Immagini Film')}
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteAllFilms} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Cancella Film' : 'Cancella Tutti i Film')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Card className={`mt-4 ${isMobile ? 'mobile-admin-card' : ''}`}>
                <Card.Header>
                    <h5 className="mb-0">Gestione Musica</h5>
                </Card.Header>
                <Card.Body>
                    <Card.Text className={isMobile ? 'mobile-warning-text' : ''}>
                        Le seguenti azioni sono permanenti e avranno effetto su tutta la musica nel database. Procedere con la massima cautela.
                    </Card.Text>
                    <div className={`d-grid gap-2 ${isMobile ? '' : 'd-md-flex justify-content-md-start'}`}>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteAllMusicaImages} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Cancella Img. Musica' : 'Cancella Immagini Musica')}
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteAllMusica} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Cancella Musica' : 'Cancella Tutta la Musica')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
            
            <Card className={`mt-4 ${isMobile ? 'mobile-admin-card' : ''}`}>
                <Card.Header>
                    <h5 className="mb-0">Gestione Clienti</h5>
                </Card.Header>
                <Card.Body>
                    <Card.Text className={isMobile ? 'mobile-warning-text' : ''}>
                        Le seguenti azioni sono permanenti e avranno effetto su tutti i clienti nel database. Procedere con la massima cautela.
                    </Card.Text>
                    <div className={`d-grid gap-2 ${isMobile ? '' : 'd-md-flex justify-content-md-start'}`}>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteAllClienti} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Cancella Clienti' : 'Cancella Tutti i Clienti')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Card className={`mt-4 ${isMobile ? 'mobile-admin-card' : ''}`}>
                <Card.Header>
                    <h5 className="mb-0">Gestione Punti Fedeltà</h5>
                </Card.Header>
                <Card.Body>
                    <Card.Text className={isMobile ? 'mobile-warning-text' : ''}>
                        Azzera tutti i punti fedeltà accumulati dai clienti ed elimina tutto lo storico transazioni. 
                        Usare questa funzione prima di andare in produzione per pulire i dati di test. 
                        <strong className="text-danger"> Questa operazione è IRREVERSIBILE.</strong>
                    </Card.Text>
                    <div className={`d-grid gap-2 ${isMobile ? '' : 'd-md-flex justify-content-md-start'}`}>
                        <Button 
                            variant="warning" 
                            onClick={handleResetAllPuntiFedelta} 
                            disabled={loading}
                            className={isMobile ? 'mobile-admin-button' : ''}
                        >
                            {loading ? <Spinner as="span" animation="border" size="sm" /> : (isMobile ? 'Azzera Punti' : 'Azzera Tutti i Punti Fedeltà')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default AdminPage;